/**
 * Run Program runtime state - the PWA-side mirror of `GET /programs` on each
 * bridge that a Run Program button points at (OPBridge DEC-524).
 *
 * Same shape as `obs/scenes.ts`, and for the same reasons: a plain signal
 * over an immutable record (never a Map - a Solid read of a Map tracks
 * nothing), keyed by BRIDGE ID because a program list belongs to a machine.
 * The studio PC's "Resolume" and the Mac's "Resolume" are two different
 * registrations with possibly different ids, and one global list would let a
 * button look configured against the wrong computer.
 *
 * # What a program button can and cannot do
 *
 * It can only run what the operator REGISTERED on that machine. Registering
 * (`PUT /admin/programs/{id}`) is admin-secret AND loopback-only on OPBridge,
 * so nothing in OPButtons can add to the list, and `GET /programs` redacts
 * argv and working directory from every paired device. A layout carries an
 * id; the machine decides what that id means.
 *
 * # Why the status is a named union and not a boolean
 *
 * - `unauthorized` - OPBridge's `opbuttons-full` bundle does not grant
 *   `/programs` + `/program/launch/*` (added in v21, OPBridge v1.15.159; an
 *   older bridge 401s). The fix is OPBridge's, so the face must say so
 *   rather than look like a missing program.
 * - `error` - anything else (a 404 on a daemon too old to have the route, a
 *   disconnected bridge, a parse failure).
 * - `unknown` - never asked. NOT the same as "no programs registered".
 *
 * There is no OBS-style `unavailable`: the list is OPBridge's own config, so
 * if the bridge answers at all it answers with the list.
 */

import { createSignal, type Accessor } from 'solid-js';
import type { ProgramEntry } from '../opbridge/httpClient.ts';

export type ProgramListStatus = 'unknown' | 'ok' | 'unauthorized' | 'error';

export interface ProgramListState {
  status: ProgramListStatus;
  /** Every registered program, in OPBridge's order. Empty on any non-`ok`
   *  status - never a stale list, which would let the Inspector offer a
   *  program that has since been removed. */
  programs: ProgramEntry[];
  /** `Date.now()` of the last completed read attempt, 0 if never. */
  at: number;
  /** Human-readable cause, for the dead-state copy. */
  detail?: string;
}

const EMPTY: ProgramListState = { status: 'unknown', programs: [], at: 0 };

const [statesSignal, setStatesSignal] = createSignal<Record<string, ProgramListState>>({});

/** The whole per-bridge mirror. Reactive. */
export const programStates: Accessor<Record<string, ProgramListState>> = statesSignal;

/** One bridge's last-known program list. Never `undefined`. */
export function programState(bridgeId: string): ProgramListState {
  return statesSignal()[bridgeId] ?? EMPTY;
}

/** Record a successful read. */
export function recordPrograms(bridgeId: string, programs: ProgramEntry[]): void {
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: { status: 'ok', programs, at: Date.now() },
  }));
}

/** Record a failed read, preserving WHICH failure. */
export function recordProgramsFailure(
  bridgeId: string,
  status: Exclude<ProgramListStatus, 'ok' | 'unknown'>,
  detail?: string,
): void {
  setStatesSignal((prev) => ({
    ...prev,
    [bridgeId]: { status, programs: [], at: Date.now(), ...(detail ? { detail } : {}) },
  }));
}

/**
 * Record a launch WE just made and the bridge acked with 200.
 *
 * Safe as an optimistic write for the same reason `recordObsSceneSwitch` is:
 * OPBridge's 200 IS the spawn ack ("the response itself is the
 * process-spawned ack", DEC-524), so the process exists by the time we get
 * here. It only flips `state` on a program we have actually read; it never
 * invents a list entry. The poll corrects it when the process exits.
 */
export function recordProgramRunning(bridgeId: string, programId: string): void {
  setStatesSignal((prev) => {
    const cur = prev[bridgeId];
    if (!cur || cur.status !== 'ok') return prev;
    if (!cur.programs.some((p) => p.id === programId)) return prev;
    return {
      ...prev,
      [bridgeId]: {
        ...cur,
        programs: cur.programs.map((p) => (p.id === programId ? { ...p, state: 'running' } : p)),
      },
    };
  });
}

// ─── Pending approvals ────────────────────────────────────────────────────

/**
 * Launches waiting for the operator to approve them on the far machine,
 * keyed `bridgeId\u0000programId`. Only an `approve`-mode program pressed
 * with a paired-device bearer lands here (OPBridge answers 202).
 *
 * Kept apart from the list because it is OUR knowledge, not the bridge's:
 * `GET /programs` reports idle/running only, so a pending request is
 * invisible to the poll and would otherwise read as "nothing happened".
 */
const [pendingSignal, setPendingSignal] = createSignal<Record<string, true>>({});

const pendingKey = (bridgeId: string, programId: string) => `${bridgeId}\u0000${programId}`;

export function recordProgramPending(bridgeId: string, programId: string): void {
  setPendingSignal((prev) => ({ ...prev, [pendingKey(bridgeId, programId)]: true }));
}

export function clearProgramPending(bridgeId: string, programId: string): void {
  setPendingSignal((prev) => {
    const k = pendingKey(bridgeId, programId);
    if (!prev[k]) return prev;
    const next = { ...prev };
    delete next[k];
    return next;
  });
}

export function programIsPending(bridgeId: string, programId: string | undefined): boolean {
  if (!programId) return false;
  return !!pendingSignal()[pendingKey(bridgeId, programId)];
}

// ─── Decisions (pure) ─────────────────────────────────────────────────────

/**
 * Why a Run Program button cannot fire, or `null` if nothing is wrong.
 *
 * THE ORDER IS THE CONTENT, same ladder as `obsButtonFault` and for the same
 * reasons:
 *
 *   no-bridge        the machine is not chosen, and the program list
 *                    belongs to a machine - so this outranks no-program.
 *                    Reachable only when there is a genuine choice; the
 *                    Inspector auto-picks the sole candidate.
 *   no-program       aimed, but no program chosen.
 *   unauthorized     we were not allowed to read the list, so we cannot know
 *                    whether the program exists. Saying "not on that
 *                    computer" here would send the operator to the wrong fix.
 *   error            any other read failure; same reasoning.
 *   program-missing  ONLY on `ok`: we read the list and it is not there -
 *                    removed on that machine, or the id belongs to another.
 *
 * `unknown` yields `null`: not having asked is not evidence of anything.
 */
export type ProgramButtonFault =
  | 'no-bridge'
  | 'no-program'
  | 'unauthorized'
  | 'error'
  | 'program-missing';

export function programButtonFault(
  programId: string | undefined,
  bridgeIds: string[] | undefined,
  st: ProgramListState,
): ProgramButtonFault | null {
  if (!bridgeIds || bridgeIds.length === 0) return 'no-bridge';
  if (!programId) return 'no-program';
  if (st.status === 'unauthorized') return 'unauthorized';
  if (st.status === 'error') return 'error';
  if (st.status === 'ok' && !st.programs.some((p) => p.id === programId)) return 'program-missing';
  return null;
}

/** Face text for a fault. Short - the Inspector carries the long version. */
export function programFaultLabel(fault: ProgramButtonFault): string {
  switch (fault) {
    case 'no-bridge':       return 'pick a computer';
    case 'no-program':      return 'pick a program';
    case 'unauthorized':    return 'no program access';
    case 'error':           return 'program list error';
    case 'program-missing': return 'not on that computer';
  }
}

/** The registered entry for `programId`, or undefined. */
export function programEntry(
  programId: string | undefined, st: ProgramListState,
): ProgramEntry | undefined {
  if (!programId || st.status !== 'ok') return undefined;
  return st.programs.find((p) => p.id === programId);
}

/** The human name for a program: its registered label, else its id. */
export function programDisplayName(p: Pick<ProgramEntry, 'id' | 'label'>): string {
  return p.label?.trim() || p.id;
}

/**
 * Is this program running on that machine right now?
 *
 * Only ever true on a status of `ok`. Unknown or failed reads false: an
 * unlit button is the honest rendering of "we do not know".
 */
export function programIsRunning(programId: string | undefined, st: ProgramListState): boolean {
  return programEntry(programId, st)?.state === 'running';
}

/**
 * Which of `bridgeIds` have at least one registered program we could read.
 * A bridge we have not asked, or that refused us, is NOT counted - it may
 * well have programs, we do not know, and counting it would let the
 * auto-pick choose on a guess.
 */
export function programCapableBridges(bridgeIds: string[]): string[] {
  const st = statesSignal();
  return bridgeIds.filter((id) => st[id]?.status === 'ok' && st[id]!.programs.length > 0);
}

/**
 * The computer a Run Program button should aim at when none is chosen, or
 * `null` to leave it unset and ask. Same two tiers as `autoPickObsBridge`:
 *
 *  1. Exactly one bridge has programs registered -> that one.
 *  2. Nothing known yet and exactly one bridge paired -> that one.
 *
 * Several candidates returns `null`. A wrong guess here answers 404 "no
 * program matching id", which reads as "the program was removed" rather than
 * "wrong computer", so it would not announce itself.
 */
export function autoPickProgramBridge(bridgeIds: string[]): string | null {
  const capable = programCapableBridges(bridgeIds);
  if (capable.length === 1) return capable[0]!;
  if (capable.length > 1) return null;
  if (bridgeIds.length === 1) return bridgeIds[0]!;
  return null;
}

/** One-line annotation for a machine in the Computer picker. Says what we
 *  KNOW about that machine's program list, never what we hope. */
export function programBridgeSummary(st: ProgramListState, connected = true): string {
  if (!connected) return 'offline';
  switch (st.status) {
    case 'ok':
      return st.programs.length === 0 ? 'no programs yet'
        : st.programs.length === 1 ? '1 program' : `${st.programs.length} programs`;
    case 'unauthorized':  return 'no program access';
    case 'error':         return 'unreachable';
    case 'unknown':       return 'not checked';
  }
}

/** Test/teardown hook. */
export function _resetProgramStateForTest(): void {
  setStatesSignal({});
  setPendingSignal({});
}
